import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Sparkles } from "lucide-react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import HitlDrawer from "@/components/hitl/HitlDrawer";
import CommandPalette from "@/components/command/CommandPalette";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import { useStore } from "@/lib/store";
import { useAuth } from "@/lib/authContext";

export default function AppShell() {
  const openOnboarding = useStore((s) => s.openOnboarding);
  const onboardingHydrated = useStore((s) => s.onboardingHydrated);
  const { isLoading } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Auto-open onboarding only AFTER the server hydration round-trip completes
  // and only if no profile was found (server or local).
  useEffect(() => {
    if (isLoading || !onboardingHydrated) return;
    const { onboarding, onboardingOpen: isOpen } = useStore.getState();
    if (!onboarding && !isOpen) {
      openOnboarding({ then: null });
    }
  }, [isLoading, onboardingHydrated, openOnboarding]);

  // Splash while either auth bootstrap or onboarding hydration is in flight.
  if (isLoading || !onboardingHydrated) {
    return (
      <div
        data-testid="appshell-splash"
        className="flex h-[100dvh] w-full items-center justify-center bg-[#F7F7F5]"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-10 w-10 animate-pulse items-center justify-center rounded-xl bg-gradient-to-br from-zinc-900 to-black">
            <Sparkles className="h-4 w-4 text-white" strokeWidth={2.4} />
          </div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Boardroom warming up…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="app-shell"
      className="flex h-[100dvh] w-full overflow-hidden bg-[#F7F7F5] text-zinc-900"
    >
      {/* Desktop sidebar (md+) */}
      <aside data-testid="sidebar-desktop" className="hidden md:flex">
        <Sidebar />
      </aside>

      {/* Mobile sidebar — Sheet drawer (<md) */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          data-testid="sidebar-sheet"
          className="w-[280px] max-w-[85vw] border-r border-black/[0.04] bg-[#FBFBFA] p-0 sm:max-w-[280px]"
        >
          <SheetTitle className="sr-only">Workspace navigation</SheetTitle>
          <Sidebar onNavigate={() => setMobileNavOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onMenuClick={() => setMobileNavOpen(true)} />
        <main
          data-testid="app-main"
          className="idea-scroll min-w-0 flex-1 overflow-y-auto"
        >
          <Outlet />
        </main>
      </div>

      {/* Globally mounted drawers / palettes */}
      <HitlDrawer />
      <CommandPalette />
    </div>
  );
}
